import { Link } from 'react-router';
import type { Route } from './+types/about';
import { SubmitLink } from '~/components/submit-modal';

export function meta(_: Route.MetaArgs) {
  return [
    { title: 'About — Tampa Bay Networking Calendar' },
    {
      name: 'description',
      content:
        'Why the Tampa Bay Networking Calendar exists: one current, comprehensive calendar of business networking across the Bay — free for organizers, free for attendees.',
    },
    { property: 'og:image', content: '/og-default.png' },
  ];
}

export default function About(_: Route.ComponentProps) {
  return (
    <main>
      {/* ── Hero — statement over a soft vector field ── */}
      <section className="about-hero">
        <AboutFlow />
        <div className="container">
          <div className="about-hero-inner">
            <p className="eyebrow about-eyebrow">About</p>
            <h1 className="about-title">
              One calendar.
              <br />
              <span className="accent">The whole Bay.</span>
            </h1>
            <p className="about-lede">
              Tampa Bay's most comprehensive calendar of business networking — kept current so you
              never have to check five websites and three Facebook groups again.
            </p>
          </div>
        </div>
        <div className="wave" aria-hidden="true">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" focusable="false">
            <path
              d="M0,44 C240,84 480,80 720,50 C960,20 1200,18 1440,50 L1440,80 L0,80 Z"
              fill="var(--paper)"
            />
          </svg>
        </div>
      </section>

      {/* ── Why this exists — editorial two-column ── */}
      <section className="band band--paper">
        <div className="container">
          <div className="about-lead-grid">
            <div className="about-lead-side">
              <span className="eyebrow">Why this exists</span>
              <span className="about-rule" aria-hidden="true" />
            </div>
            <div className="about-lead-body">
              <p className="about-lead-p">
                Tampa Bay has a huge networking scene — chamber breakfasts, BNI chapters, industry
                meetups, nonprofit mixers, workshops — but it's scattered across dozens of sites,
                newsletters, and group chats. Events get missed. Rooms that should be full aren't.
              </p>
              <p>This calendar puts all of it in one place, in one format, always up to date.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Image band — the skyline, framed ── */}
      <section className="about-figure" aria-hidden="true">
        <div className="about-figure-media" />
        <div className="container">
          <p className="about-figure-quote">
            The Bay looks here <span className="accent">first</span>.
          </p>
        </div>
      </section>

      {/* ── For attendees / For organizers ── */}
      <section className="band band--paper">
        <div className="container">
          <div className="about-two">
            <article className="about-col">
              <span className="about-col-num" aria-hidden="true">
                01
              </span>
              <h2>For attendees</h2>
              <p>
                Everything is free to browse — no account, no email wall. Filter by area, type, and
                cost, subscribe to the live calendar feed, or grab the Monday digest and plan your
                week in thirty seconds.
              </p>
              <Link to="/" className="btn-link">
                Browse the calendar →
              </Link>
            </article>

            <article className="about-col">
              <span className="about-col-num" aria-hidden="true">
                02
              </span>
              <h2>For organizers</h2>
              <p>
                Listing is free and takes about two minutes — paste a link or upload your flyer and
                the form fills itself. You get a private edit link for every listing, so updates and
                cancellations are one click, no login. Recurring events are one submission, not
                twelve. If you run events in the Bay, this calendar works hardest when <em>all</em>{' '}
                of yours are on it.
              </p>
              <SubmitLink className="ui-btn btn-primary">List your event — free</SubmitLink>
            </article>
          </div>
        </div>
      </section>

      {/* ── What we list — and what we don't ── */}
      <section className="band band--gray">
        <div className="container">
          <div className="about-standard">
            <div className="about-standard-head">
              <span className="eyebrow">Our standard</span>
              <h2 className="section-title">What we list — and what we don't</h2>
            </div>
            <div className="about-standard-body">
              <p>
                This calendar is for business networking and professional development: mixers,
                referral groups, chamber events, industry meetups, workshops, and nonprofit events
                with a professional audience. Every submission is reviewed by a person before it
                goes live.
              </p>
              <p>
                We decline listings that are purely promotional pitches, unrelated to professional
                life, or misleading about cost or content — and we reserve the right to decline any
                listing to keep the calendar useful.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Talk to us — closing CTA ── */}
      <section className="band band--ink about-talk">
        <div className="container">
          <div className="about-talk-inner">
            <h2 className="about-talk-title">Talk to us</h2>
            <p className="about-talk-copy">
              Corrections, questions, an event we're missing? A real person reads every message.
            </p>
            <div className="row about-talk-actions">
              <Link to="/contact" className="ui-btn btn-primary btn-lg">
                Send a note
              </Link>
              <SubmitLink className="ui-btn btn-secondary on-dark btn-lg">List an event</SubmitLink>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function AboutFlow() {
  return (
    <svg
      className="about-flow"
      viewBox="0 0 1440 560"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="none" stroke="rgba(22,163,74,0.5)" strokeWidth="1.4">
        <path d="M-40,180 C300,120 560,320 900,240 C1160,180 1360,300 1500,240" />
        <path d="M-40,260 C320,200 600,400 940,320 C1200,260 1400,380 1540,320" />
        <path d="M-40,340 C340,280 640,480 980,400 C1240,340 1440,460 1580,400" />
      </g>
      <g fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1">
        <path d="M-40,220 C300,160 560,360 900,280 C1160,220 1360,340 1500,280" />
        <path d="M-40,300 C320,240 600,440 940,360 C1200,300 1400,420 1540,360" />
      </g>
    </svg>
  );
}
